import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { fetchCategoriesWithSubCategories } from '../features/categories/categoriesSlice';
import { fetchProducts } from '../features/products/productsSlice';
import { addToCart, addToCartAsync, fetchCart, updateQuantity, removeFromCart, updateCartItemAsync, removeFromCartAsync } from '../features/cart/cartSlice';
import Header from './Header';
import Footer from './Footer';
import HomeSlider from './HomeSlider';
import axios from 'axios';
import { API_URL, BROKEN_IMAGE_PLACEHOLDER, getImageUrl as getImageUrlFromConfig, normalizeList } from '../config';
import './Home.css';
import placeholderImage from '../images/Kurti1.avif';
import { isYouTubeUrl, getYouTubeWatchUrl } from '../utils/productHelpers';

// Helper to get property value supporting both PascalCase and camelCase
const getProp = (obj, camelCase) => {
  if (!obj) return undefined;
  const pascalCase = camelCase.charAt(0).toUpperCase() + camelCase.slice(1);
  return obj[camelCase] !== undefined ? obj[camelCase] : obj[pascalCase];
};

const getImageUrl = (url) => getImageUrlFromConfig(url, placeholderImage);

const getProductVariants = (product) =>
  normalizeList(getProp(product, 'variants') || getProp(product, 'Variants'));

const getVariantStockGroups = (product) => {
  const groups = [];
  const byColor = new Map();
  getProductVariants(product).forEach((variant) => {
    const color = getProp(variant, 'colorName') || '—';
    const size = getProp(variant, 'sizeName') || '';
    const stock = Number(getProp(variant, 'stock') ?? 0);
    if (!byColor.has(color)) {
      const group = { color, sizes: [] };
      byColor.set(color, group);
      groups.push(group);
    }
    byColor.get(color).sizes.push({ size, stock });
  });
  return groups;
};

const getProductColorNames = (product) => {
  const names = getVariantStockGroups(product)
    .map((group) => group.color)
    .filter((name) => name && name !== '—');
  return [...new Set(names)];
};

const getProductSizeNames = (product) => {
  const names = getProductVariants(product)
    .map((variant) => getProp(variant, 'sizeName'))
    .filter((name) => name);
  return [...new Set(names)];
};

const getEffectiveStock = (product) => {
  const variants = getProductVariants(product);
  if (variants.length > 0) {
    return variants.reduce((sum, variant) => sum + Number(getProp(variant, 'stock') || 0), 0);
  }
  return Number(getProp(product, 'stock') || product.stockQuantity || 0);
};

/**
 * Wait until a seek completes (or timeout). Some MP4s only decode after a keyframe past t=0.
 */
function waitSeeked(video, targetTime, timeoutMs = 5000) {
  return new Promise((resolve) => {
    let settled = false;
    const done = () => {
      if (settled) return;
      settled = true;
      clearTimeout(tid);
      video.removeEventListener('seeked', onSeeked);
      resolve();
    };

    const onSeeked = () => done();
    const tid = setTimeout(done, timeoutMs);

    try {
      const dur = video.duration;
      let t = targetTime;
      if (Number.isFinite(dur) && dur > 0) {
        t = Math.min(Math.max(0, targetTime), Math.max(0, dur - 0.001));
      }
      if (Number.isNaN(t)) {
        done();
        return;
      }
      if (Math.abs((video.currentTime || 0) - t) < 0.02) {
        requestAnimationFrame(done);
        return;
      }
      video.addEventListener('seeked', onSeeked, { once: true });
      video.currentTime = t;
    } catch {
      done();
    }
  });
}

function waitNextPaint(video) {
  return new Promise((resolve) => {
    if (typeof video.requestVideoFrameCallback === 'function') {
      video.requestVideoFrameCallback(() => resolve());
    } else {
      requestAnimationFrame(() => resolve());
    }
  });
}

/**
 * Product image sits behind the video until a frame is decoded. Many browsers paint black over
 * `<video poster>` once metadata loads; hiding the video (opacity 0) until `videoWidth` / playback
 * keeps the tile from looking blank. Tap/Enter starts playback if auto-prime did not surface a frame.
 */
function HomeVideoPreview({ src, className, fallbackPosterUrl }) {
  const ref = useRef(null);
  const [mediaError, setMediaError] = useState(false);
  const [frameVisible, setFrameVisible] = useState(false);

  const validFallback =
    fallbackPosterUrl &&
    fallbackPosterUrl !== BROKEN_IMAGE_PLACEHOLDER;

  useEffect(() => {
    setMediaError(false);
    setFrameVisible(false);
  }, [src]);

  useEffect(() => {
    const el = ref.current;
    if (!el || !src || mediaError) return;

    let cancelled = false;
    const isCancelled = () => cancelled;

    const revealIfDecoded = () => {
      if (isCancelled()) return;
      if (el.videoWidth > 0 && el.videoHeight > 0) {
        setFrameVisible(true);
      }
    };

    const primeOnce = async () => {
      const wasMuted = el.muted;
      try {
        el.muted = true;

        const dur = el.duration;
        const peek =
          Number.isFinite(dur) && dur > 0
            ? Math.min(0.35, Math.max(0.06, dur * 0.04))
            : 0.12;

        await waitSeeked(el, peek);
        if (isCancelled()) return;

        try {
          await el.play();
        } catch {
          await new Promise((r) => setTimeout(r, 400));
          if (!isCancelled()) await el.play().catch(() => {});
        }

        if (isCancelled()) return;
        el.pause();

        await waitSeeked(el, 0);
        if (isCancelled()) return;

        el.muted = wasMuted;
        await waitNextPaint(el);
        revealIfDecoded();
      } catch {
        try {
          el.muted = false;
        } catch {
          /* ignore */
        }
      }
    };

    let earlyDone = false;

    const runEarlyPrime = async () => {
      if (earlyDone) return;
      earlyDone = true;
      await primeOnce();
    };

    const runLatePrime = async () => {
      if (isCancelled() || el.videoWidth > 0) return;
      await primeOnce();
    };

    const onLoadedData = () => {
      revealIfDecoded();
      void runEarlyPrime();
    };
    const onCanPlay = () => void runEarlyPrime();
    const onCanPlayThrough = () => void runLatePrime();
    const onPlaying = () => setFrameVisible(true);

    el.addEventListener('loadeddata', onLoadedData, { once: true });
    el.addEventListener('canplay', onCanPlay, { once: true });
    el.addEventListener('canplaythrough', onCanPlayThrough, { once: true });
    el.addEventListener('playing', onPlaying);

    return () => {
      cancelled = true;
      el.removeEventListener('loadeddata', onLoadedData);
      el.removeEventListener('canplay', onCanPlay);
      el.removeEventListener('canplaythrough', onCanPlayThrough);
      el.removeEventListener('playing', onPlaying);
    };
  }, [src, mediaError, fallbackPosterUrl]);

  const tryPlayFromUser = () => {
    const v = ref.current;
    if (!v || frameVisible) return;
    v.muted = true;
    void v.play().catch(() => {});
  };

  if (mediaError) {
    return validFallback ? (
      <div className="home-video-stack">
        <img
          className="home-video-fallback-img"
          src={fallbackPosterUrl}
          alt=""
          onError={(e) => {
            e.target.src = BROKEN_IMAGE_PLACEHOLDER;
          }}
        />
      </div>
    ) : (
      <div className="home-video-stack home-video-unavailable" role="status">
        Video unavailable
      </div>
    );
  }

  return (
    <div
      className={`home-video-stack${frameVisible ? ' is-frame-visible' : ''}`}
      role={!frameVisible ? 'button' : undefined}
      tabIndex={!frameVisible ? 0 : undefined}
      aria-label={!frameVisible ? 'Play product video preview' : undefined}
      onClick={(e) => {
        e.stopPropagation();
        tryPlayFromUser();
      }}
      onKeyDown={(e) => {
        if (frameVisible) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          tryPlayFromUser();
        }
      }}
    >
      {validFallback ? (
        <img
          className="home-video-fallback-img"
          src={fallbackPosterUrl}
          alt=""
          draggable={false}
          onError={(e) => {
            e.currentTarget.style.visibility = 'hidden';
          }}
        />
      ) : null}
      {!frameVisible ? (
        <div className="home-video-play-hint" aria-hidden="true">
          <span className="home-video-play-icon">▶</span>
        </div>
      ) : null}
      <video
        ref={ref}
        key={src}
        className={`${className}${frameVisible ? ' home-video-frame-ready' : ' home-video-awaiting-frame'}`}
        src={src}
        controls
        playsInline
        preload="auto"
        onError={() => setMediaError(true)}
      />
    </div>
  );
}

const VIDEO_CAROUSEL_INTERVAL_MS = 4200;
const VIDEO_DESKTOP_BREAKPOINT = 769;

function HomeVideosCarousel({ products }) {
  const [slidesPerView, setSlidesPerView] = useState(() => (
    typeof window !== 'undefined'
      ? window.matchMedia(`(min-width: ${VIDEO_DESKTOP_BREAKPOINT}px)`).matches
      : false
  ) ? 2 : 1);

  useEffect(() => {
    const mq = window.matchMedia(`(min-width: ${VIDEO_DESKTOP_BREAKPOINT}px)`);
    const onChange = () => setSlidesPerView(mq.matches ? 2 : 1);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const step = slidesPerView === 2 && products.length % 2 === 0 ? 2 : 1;
  const canCarousel = products.length > slidesPerView;
  const pageCount = step === 2
    ? products.length / 2
    : products.length;

  const loopedProducts = useMemo(() => {
    if (!canCarousel) return products;
    const head = products.slice(-slidesPerView);
    const tail = products.slice(0, slidesPerView);
    return [...head, ...products, ...tail];
  }, [canCarousel, products, slidesPerView]);

  const displayProducts = canCarousel ? loopedProducts : products;

  const [activeIndex, setActiveIndex] = useState(canCarousel ? slidesPerView : 0);
  const [isTransitionEnabled, setIsTransitionEnabled] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const trackRef = useRef(null);
  const activeIndexRef = useRef(activeIndex);
  const touchStartRef = useRef(null);
  const touchEndRef = useRef(null);

  const currentSlide = canCarousel
    ? (activeIndex - slidesPerView + products.length) % products.length
    : 0;

  const currentPage = canCarousel
    ? Math.floor((activeIndex - slidesPerView) / step) % pageCount
    : 0;

  useEffect(() => {
    activeIndexRef.current = activeIndex;
  }, [activeIndex]);

  const syncTrackPosition = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const slide = track.querySelector('.home-videos-carousel-slide');
    if (!slide) return;
    const slideWidth = slide.offsetWidth;
    if (slideWidth <= 0) return;
    const trackStyles = window.getComputedStyle(track);
    const gap = parseFloat(trackStyles.columnGap || trackStyles.gap || 0) || 0;
    track.style.transform = `translateX(-${activeIndexRef.current * (slideWidth + gap)}px)`;
  }, []);

  useEffect(() => {
    setIsTransitionEnabled(false);
    setActiveIndex(canCarousel ? slidesPerView : 0);
  }, [products.length, slidesPerView, canCarousel]);

  useEffect(() => {
    syncTrackPosition();
    window.addEventListener('resize', syncTrackPosition);
    return () => window.removeEventListener('resize', syncTrackPosition);
  }, [activeIndex, displayProducts.length, syncTrackPosition, slidesPerView]);

  useEffect(() => {
    if (isTransitionEnabled) return undefined;
    const timer = window.setTimeout(() => setIsTransitionEnabled(true), 40);
    return () => window.clearTimeout(timer);
  }, [isTransitionEnabled]);

  const nextSlide = useCallback(() => {
    if (!canCarousel) return;
    setIsTransitionEnabled(true);
    setActiveIndex((prev) => prev + step);
  }, [canCarousel, step]);

  const prevSlide = useCallback(() => {
    if (!canCarousel) return;
    setIsTransitionEnabled(true);
    setActiveIndex((prev) => prev - step);
  }, [canCarousel, step]);

  const goToPage = (pageIndex) => {
    if (!canCarousel || pageIndex === currentPage) return;
    setIsTransitionEnabled(true);
    setActiveIndex(slidesPerView + pageIndex * step);
  };

  useEffect(() => {
    if (!canCarousel || isPaused) return undefined;
    const timer = setInterval(nextSlide, VIDEO_CAROUSEL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [canCarousel, isPaused, nextSlide]);

  const handleTransitionEnd = useCallback((event) => {
    if (event.propertyName !== 'transform' || !canCarousel) return;
    const index = activeIndexRef.current;
    if (index < slidesPerView) {
      setIsTransitionEnabled(false);
      setActiveIndex(index + products.length);
      return;
    }
    if (index >= slidesPerView + products.length) {
      setIsTransitionEnabled(false);
      setActiveIndex(index - products.length);
    }
  }, [canCarousel, products.length, slidesPerView]);

  const handleTouchStart = (event) => {
    touchStartRef.current = event.targetTouches[0].clientX;
    touchEndRef.current = null;
  };

  const handleTouchMove = (event) => {
    touchEndRef.current = event.targetTouches[0].clientX;
  };

  const handleTouchEnd = () => {
    const start = touchStartRef.current;
    const end = touchEndRef.current;
    if (start == null || end == null) return;
    const distance = start - end;
    if (Math.abs(distance) > 50) {
      if (distance > 0) nextSlide();
      else prevSlide();
    }
    touchStartRef.current = null;
    touchEndRef.current = null;
  };

  const renderVideoSlide = (product) => {
    const productName = getProp(product, 'productName');
    const rawVideo = getProp(product, 'videoUrl');
    const isYoutube = isYouTubeUrl(rawVideo);
    const youtubeWatchUrl = isYoutube ? getYouTubeWatchUrl(rawVideo) : '';
    const videoSrc = rawVideo && !isYoutube ? getImageUrl(rawVideo) : '';
    const thumbRaw = getProp(product, 'videoThumbnailUrl');
    const posterUrl = thumbRaw
      ? getImageUrl(thumbRaw)
      : getImageUrl(getProp(product, 'imageUrl'));

    return (
      <article className="home-videos-carousel-card">
        <div className="home-videos-carousel-media">
          {isYoutube ? (
            <button
              type="button"
              className="home-video-youtube-link"
              onClick={() => window.open(youtubeWatchUrl, '_blank', 'noopener,noreferrer')}
              aria-label={`Watch ${productName} on YouTube`}
            >
              <img
                src={posterUrl}
                alt=""
                className="home-video-youtube-thumb"
                onLoad={syncTrackPosition}
                onError={(e) => {
                  e.currentTarget.src = BROKEN_IMAGE_PLACEHOLDER;
                }}
              />
              <span className="home-video-youtube-play-badge" aria-hidden="true">
                <svg viewBox="0 0 68 48" width="68" height="48" focusable="false">
                  <path
                    d="M66.52 7.74a8.28 8.28 0 0 0-5.82-5.85C55.3 1 34 1 34 1S12.7 1 7.3 1.89A8.28 8.28 0 0 0 1.48 7.74C.57 13.14.57 24 .57 24s0 10.86.91 16.26a8.28 8.28 0 0 0 5.82 5.85C12.7 47 34 47 34 47s21.3 0 26.7-.89a8.28 8.28 0 0 0 5.82-5.85C67.43 34.86 67.43 24 67.43 24s0-10.86-.91-16.26z"
                    fill="#f00"
                  />
                  <path d="M27 34V14l18 10-18 10z" fill="#fff" />
                </svg>
              </span>
            </button>
          ) : videoSrc ? (
            <HomeVideoPreview
              className="home-video-player"
              src={videoSrc}
              fallbackPosterUrl={posterUrl}
            />
          ) : posterUrl ? (
            <img
              src={posterUrl}
              alt=""
              className="home-video-youtube-thumb"
            />
          ) : null}
        </div>
      </article>
    );
  };

  return (
    <div
      className="home-videos-carousel"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {canCarousel && slidesPerView > 1 && (
        <>
          <button
            type="button"
            className="home-videos-carousel-arrow prev"
            onClick={prevSlide}
            aria-label="Previous videos"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <button
            type="button"
            className="home-videos-carousel-arrow next"
            onClick={nextSlide}
            aria-label="Next videos"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>
        </>
      )}
      <div className="home-videos-carousel-viewport">
        <div
          ref={trackRef}
          className={`home-videos-carousel-track${isTransitionEnabled ? '' : ' no-transition'}`}
          onTransitionEnd={handleTransitionEnd}
        >
          {displayProducts.map((product, index) => {
            const productId = getProp(product, 'productId');
            const slideIndex = canCarousel
              ? (index - slidesPerView + products.length) % products.length
              : index;
            const isActive = slidesPerView === 1 && slideIndex === currentSlide;

            return (
              <div
                key={`home-vid-slide-${productId}-${index}`}
                className={`home-videos-carousel-slide${isActive ? ' is-active' : ''}`}
              >
                {renderVideoSlide(product)}
              </div>
            );
          })}
        </div>
      </div>
      {canCarousel && (
        <div className="home-videos-carousel-dots" role="tablist" aria-label="Video slides">
          {Array.from({ length: pageCount }, (_, pageIndex) => (
            <button
              key={`home-vid-dot-page-${pageIndex}`}
              type="button"
              role="tab"
              aria-selected={pageIndex === currentPage}
              aria-label={`Go to video page ${pageIndex + 1}`}
              className={`home-videos-carousel-dot${pageIndex === currentPage ? ' active' : ''}`}
              onClick={() => goToPage(pageIndex)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/** Measure one horizontal step inside a scroll viewport (card width + gap). */
function getHorizontalScrollStep(viewport, itemSelector, trackSelector = null) {
  if (!viewport) return 0;
  const track = trackSelector
    ? viewport.querySelector(trackSelector)
    : viewport.firstElementChild;
  const item = track?.querySelector(itemSelector);
  if (!track || !item) return 0;
  const style = getComputedStyle(track);
  const gap =
    parseFloat(style.columnGap) ||
    parseFloat(style.gap) ||
    8;
  return item.getBoundingClientRect().width + gap;
}

/** Measure one brand column step on the 2-row grid track. */
function getBrandTrackStep(track) {
  if (!track) return 0;
  const cards = track.querySelectorAll('.home-brand-card');
  if (cards.length === 0) return 0;
  if (cards.length >= 3) {
    const step = cards[2].offsetLeft - cards[0].offsetLeft;
    if (step > 0) return step;
  }
  const columnGap = parseFloat(getComputedStyle(track).columnGap) || 8;
  return cards[0].offsetWidth + columnGap;
}

/** Brands grid: 2 rows per column — step one full column (not one card). */
function getBrandColumnScrollStep(viewport) {
  const track = viewport?.querySelector('.home-brands-wrap');
  return getBrandTrackStep(track);
}

/** Scroll brands by one column; instant scroll keeps exactly two columns visible during auto-advance. */
function scrollBrandsViewport(viewport, direction) {
  const step = getBrandColumnScrollStep(viewport);
  if (!viewport || step <= 0) return;

  const maxScroll = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
  const nextLeft = viewport.scrollLeft + direction * step;

  if (direction > 0 && nextLeft >= maxScroll - 2) {
    viewport.scrollTo({ left: 0, behavior: 'auto' });
    return;
  }
  if (direction < 0 && nextLeft <= 0) {
    viewport.scrollTo({ left: maxScroll, behavior: 'auto' });
    return;
  }
  viewport.scrollTo({ left: nextLeft, behavior: 'auto' });
}

/** Scroll a horizontal viewport by one step; loops at the ends. */
function scrollHorizontalViewport(viewport, direction, itemSelector, trackSelector = null) {
  const step = getHorizontalScrollStep(viewport, itemSelector, trackSelector);
  if (!viewport || step <= 0) return;

  const maxScroll = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
  const nextLeft = viewport.scrollLeft + direction * step;

  if (direction > 0 && nextLeft >= maxScroll - 2) {
    viewport.scrollTo({ left: 0, behavior: 'smooth' });
    return;
  }
  if (direction < 0 && nextLeft <= 0) {
    viewport.scrollTo({ left: maxScroll, behavior: 'smooth' });
    return;
  }
  viewport.scrollTo({ left: nextLeft, behavior: 'smooth' });
}

const MARQUEE_HOLD_MS = 1400;
const MARQUEE_MOVE_MS = 550;
const CATEGORY_DESKTOP_BREAKPOINT = 769;

function HomeCategoriesRow({
  categories,
  categoryImages,
  categoryImageErrors,
  onCategoryImageError,
  isPaused,
}) {
  const navigate = useNavigate();
  const [isDesktop, setIsDesktop] = useState(() => (
    typeof window !== 'undefined'
      ? window.matchMedia(`(min-width: ${CATEGORY_DESKTOP_BREAKPOINT}px)`).matches
      : false
  ));

  useEffect(() => {
    const mq = window.matchMedia(`(min-width: ${CATEGORY_DESKTOP_BREAKPOINT}px)`);
    const onChange = () => setIsDesktop(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const canScroll = categories.length > 1;
  const loopedCategories = useMemo(() => {
    if (!canScroll) return categories;
    return [...categories, ...categories];
  }, [canScroll, categories]);

  const mobileViewportRef = useRef(null);
  const trackRef = useRef(null);
  const desktopIndexRef = useRef(0);
  const mobileTimersRef = useRef([]);
  const desktopTimersRef = useRef([]);

  const [desktopIndex, setDesktopIndex] = useState(0);
  const [isTransitionEnabled, setIsTransitionEnabled] = useState(true);

  const syncDesktopTrack = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const item = track.querySelector('.home-category-slide-item');
    if (!item) return;
    const trackStyles = window.getComputedStyle(track);
    const gap = parseFloat(trackStyles.columnGap || trackStyles.gap || 0) || 0;
    const step = item.offsetWidth + gap;
    if (step <= 0) return;
    track.style.transform = `translateX(-${desktopIndexRef.current * step}px)`;
  }, []);

  useLayoutEffect(() => {
    if (!isDesktop) return;
    syncDesktopTrack();
  }, [isDesktop, desktopIndex, isTransitionEnabled, loopedCategories.length, syncDesktopTrack]);

  useEffect(() => {
    if (!isDesktop) return undefined;
    const onResize = () => syncDesktopTrack();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [isDesktop, syncDesktopTrack]);

  useEffect(() => {
    desktopIndexRef.current = desktopIndex;
  }, [desktopIndex]);

  useEffect(() => {
    if (!isDesktop) {
      setDesktopIndex(0);
      desktopIndexRef.current = 0;
      return;
    }
    setIsTransitionEnabled(false);
    setDesktopIndex(0);
    desktopIndexRef.current = 0;
  }, [isDesktop, categories.length]);

  useLayoutEffect(() => {
    if (!isDesktop || isTransitionEnabled) return undefined;
    const frame = window.requestAnimationFrame(() => setIsTransitionEnabled(true));
    return () => window.cancelAnimationFrame(frame);
  }, [isDesktop, isTransitionEnabled]);

  const advanceDesktop = useCallback(() => {
    if (!canScroll || !isDesktop) return;
    setIsTransitionEnabled(true);
    setDesktopIndex((prev) => prev + 1);
  }, [canScroll, isDesktop]);

  const handleDesktopTransitionEnd = useCallback((event) => {
    if (!isDesktop || event.propertyName !== 'transform') return;
    if (desktopIndexRef.current < categories.length) return;
    desktopIndexRef.current = 0;
    setIsTransitionEnabled(false);
    setDesktopIndex(0);
  }, [isDesktop, categories.length]);

  useEffect(() => {
    if (!isDesktop || !canScroll || isPaused) {
      desktopTimersRef.current.forEach((id) => clearTimeout(id));
      desktopTimersRef.current = [];
      return undefined;
    }

    let cancelled = false;
    const queue = (fn, delay) => {
      const id = setTimeout(() => {
        if (!cancelled) fn();
      }, delay);
      desktopTimersRef.current.push(id);
    };

    const runCycle = () => {
      queue(() => {
        advanceDesktop();
        queue(runCycle, MARQUEE_MOVE_MS + MARQUEE_HOLD_MS);
      }, MARQUEE_HOLD_MS);
    };

    runCycle();

    return () => {
      cancelled = true;
      desktopTimersRef.current.forEach((id) => clearTimeout(id));
      desktopTimersRef.current = [];
    };
  }, [isDesktop, canScroll, isPaused, advanceDesktop]);

  useEffect(() => {
    if (isDesktop || !canScroll || isPaused) {
      mobileTimersRef.current.forEach((id) => clearTimeout(id));
      mobileTimersRef.current = [];
      return undefined;
    }

    let cancelled = false;
    const queue = (fn, delay) => {
      const id = setTimeout(() => {
        if (!cancelled) fn();
      }, delay);
      mobileTimersRef.current.push(id);
    };

    const scrollMobile = (direction) => {
      scrollHorizontalViewport(
        mobileViewportRef.current,
        direction,
        '.home-category-slide-item',
        '.home-categories-slider'
      );
    };

    const runCycle = () => {
      queue(() => {
        scrollMobile(1);
        queue(runCycle, MARQUEE_MOVE_MS + MARQUEE_HOLD_MS);
      }, MARQUEE_HOLD_MS);
    };

    runCycle();

    return () => {
      cancelled = true;
      mobileTimersRef.current.forEach((id) => clearTimeout(id));
      mobileTimersRef.current = [];
    };
  }, [isDesktop, canScroll, isPaused]);

  const displayCategories = isDesktop && canScroll ? loopedCategories : categories;

  return (
    <div
      className={`home-categories-viewport home-scroll-viewport${isDesktop ? ' home-categories-marquee-viewport' : ''}`}
      ref={isDesktop ? null : mobileViewportRef}
    >
      <div
        ref={isDesktop ? trackRef : null}
        className={`home-categories-slider${isDesktop && canScroll ? ' home-categories-marquee-track' : ''}${isDesktop && canScroll && !isTransitionEnabled ? ' no-transition' : ''}`}
        onTransitionEnd={isDesktop ? handleDesktopTransitionEnd : undefined}
      >
        {displayCategories.map((cat, idx) => {
          const categoryImg = cat.imageUrl || cat.ImageUrl;
          const fallbackImg = categoryImages[cat.categoryId];
          const imgSrc = categoryImg ? getImageUrl(categoryImg) : fallbackImg;
          const showImage = !!imgSrc && !categoryImageErrors[cat.categoryId];

          return (
            <button
              key={`${cat.categoryId}-${idx}`}
              type="button"
              className="home-category-slide-item"
              onClick={() => navigate(`/category/${cat.categoryId}`)}
            >
              <span className="home-category-image-circle">
                {showImage ? (
                  <img
                    src={imgSrc}
                    alt={cat.categoryName}
                    onLoad={isDesktop ? syncDesktopTrack : undefined}
                    onError={() => onCategoryImageError(cat.categoryId)}
                  />
                ) : (
                  <span className="home-category-image-fallback">
                    {String(cat.categoryName || 'C').charAt(0).toUpperCase()}
                  </span>
                )}
              </span>
              <span className="home-category-title">{cat.categoryName}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function HomeBrandsRow({ brands, isPaused }) {
  const navigate = useNavigate();
  const [isDesktop, setIsDesktop] = useState(() => (
    typeof window !== 'undefined'
      ? window.matchMedia(`(min-width: ${CATEGORY_DESKTOP_BREAKPOINT}px)`).matches
      : false
  ));

  useEffect(() => {
    const mq = window.matchMedia(`(min-width: ${CATEGORY_DESKTOP_BREAKPOINT}px)`);
    const onChange = () => setIsDesktop(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const columnCount = Math.ceil(brands.length / 2);
  const canScroll = columnCount > 1;
  const loopedBrands = useMemo(() => {
    if (!canScroll) return brands;
    return [...brands, ...brands];
  }, [canScroll, brands]);

  const mobileViewportRef = useRef(null);
  const trackRef = useRef(null);
  const desktopIndexRef = useRef(0);
  const mobileTimersRef = useRef([]);
  const desktopTimersRef = useRef([]);

  const [desktopIndex, setDesktopIndex] = useState(0);
  const [isTransitionEnabled, setIsTransitionEnabled] = useState(true);

  const syncDesktopTrack = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const step = getBrandTrackStep(track);
    if (step <= 0) return;
    track.style.transform = `translateX(-${desktopIndexRef.current * step}px)`;
  }, []);

  useLayoutEffect(() => {
    if (!isDesktop) return;
    syncDesktopTrack();
  }, [isDesktop, desktopIndex, isTransitionEnabled, loopedBrands.length, syncDesktopTrack]);

  useEffect(() => {
    if (!isDesktop) return undefined;
    const onResize = () => syncDesktopTrack();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [isDesktop, syncDesktopTrack]);

  useEffect(() => {
    desktopIndexRef.current = desktopIndex;
  }, [desktopIndex]);

  useEffect(() => {
    if (!isDesktop) {
      setDesktopIndex(0);
      desktopIndexRef.current = 0;
      return;
    }
    setIsTransitionEnabled(false);
    setDesktopIndex(0);
    desktopIndexRef.current = 0;
  }, [isDesktop, brands.length]);

  useLayoutEffect(() => {
    if (!isDesktop || isTransitionEnabled) return undefined;
    const frame = window.requestAnimationFrame(() => setIsTransitionEnabled(true));
    return () => window.cancelAnimationFrame(frame);
  }, [isDesktop, isTransitionEnabled]);

  const advanceDesktop = useCallback(() => {
    if (!canScroll || !isDesktop) return;
    setIsTransitionEnabled(true);
    setDesktopIndex((prev) => prev + 1);
  }, [canScroll, isDesktop]);

  const handleDesktopTransitionEnd = useCallback((event) => {
    if (!isDesktop || event.propertyName !== 'transform') return;
    if (desktopIndexRef.current < columnCount) return;
    desktopIndexRef.current = 0;
    setIsTransitionEnabled(false);
    setDesktopIndex(0);
  }, [isDesktop, columnCount]);

  useEffect(() => {
    if (!isDesktop || !canScroll || isPaused) {
      desktopTimersRef.current.forEach((id) => clearTimeout(id));
      desktopTimersRef.current = [];
      return undefined;
    }

    let cancelled = false;
    const queue = (fn, delay) => {
      const id = setTimeout(() => {
        if (!cancelled) fn();
      }, delay);
      desktopTimersRef.current.push(id);
    };

    const runCycle = () => {
      queue(() => {
        advanceDesktop();
        queue(runCycle, MARQUEE_MOVE_MS + MARQUEE_HOLD_MS);
      }, MARQUEE_HOLD_MS);
    };

    runCycle();

    return () => {
      cancelled = true;
      desktopTimersRef.current.forEach((id) => clearTimeout(id));
      desktopTimersRef.current = [];
    };
  }, [isDesktop, canScroll, isPaused, advanceDesktop]);

  useEffect(() => {
    if (isDesktop || !canScroll || isPaused) {
      mobileTimersRef.current.forEach((id) => clearTimeout(id));
      mobileTimersRef.current = [];
      return undefined;
    }

    let cancelled = false;
    const queue = (fn, delay) => {
      const id = setTimeout(() => {
        if (!cancelled) fn();
      }, delay);
      mobileTimersRef.current.push(id);
    };

    const scrollMobile = (direction) => {
      scrollBrandsViewport(mobileViewportRef.current, direction);
    };

    const runCycle = () => {
      queue(() => {
        scrollMobile(1);
        queue(runCycle, MARQUEE_MOVE_MS + MARQUEE_HOLD_MS);
      }, MARQUEE_HOLD_MS);
    };

    runCycle();

    return () => {
      cancelled = true;
      mobileTimersRef.current.forEach((id) => clearTimeout(id));
      mobileTimersRef.current = [];
    };
  }, [isDesktop, canScroll, isPaused]);

  const displayBrands = isDesktop && canScroll ? loopedBrands : brands;

  return (
    <div
      className={`home-brands-viewport home-scroll-viewport${isDesktop ? ' home-brands-marquee-viewport' : ''}`}
      ref={isDesktop ? null : mobileViewportRef}
    >
      <div
        ref={isDesktop ? trackRef : null}
        className={`home-brands-wrap home-brands-track${isDesktop && canScroll ? ' home-brands-marquee-track' : ''}${isDesktop && canScroll && !isTransitionEnabled ? ' no-transition' : ''}`}
        onTransitionEnd={isDesktop ? handleDesktopTransitionEnd : undefined}
      >
        {displayBrands.map((brand, idx) => (
          <button
            key={`${getProp(brand, 'brandName')}-${getProp(brand, 'productId')}-${idx}`}
            type="button"
            className="home-brand-card"
            onClick={() => {
              const name = getProp(brand, 'brandName') || '';
              navigate(`/brand/${encodeURIComponent(name)}`, {
                state: {
                  brandImageUrl: getImageUrl(getProp(brand, 'brandImageUrl')),
                },
              });
            }}
            aria-label={getProp(brand, 'brandName')}
          >
            <span className="home-brand-image-wrap">
              <img
                src={getImageUrl(getProp(brand, 'brandImageUrl'))}
                alt={getProp(brand, 'brandName')}
                onLoad={isDesktop ? syncDesktopTrack : undefined}
                onError={(e) => { e.target.src = BROKEN_IMAGE_PLACEHOLDER; }}
              />
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * RCM-style product row: native horizontal scroll (arrows + touch) with pause → slide auto-advance.
 */
function HomeProductMarqueeRow({ products, renderCard, isPaused, className = '', showNavArrows = true }) {
  const viewportRef = useRef(null);
  const timersRef = useRef([]);
  const n = products?.length ?? 0;
  const canScroll = n > 1;

  const clearTimers = useCallback(() => {
    timersRef.current.forEach((id) => clearTimeout(id));
    timersRef.current = [];
  }, []);

  const scrollByDirection = useCallback((direction) => {
    scrollHorizontalViewport(
      viewportRef.current,
      direction,
      '.home-marquee-item',
      '.home-marquee-track'
    );
  }, []);

  useEffect(() => {
    if (!canScroll || isPaused) {
      clearTimers();
      return undefined;
    }

    let cancelled = false;
    const queue = (fn, delay) => {
      const id = setTimeout(() => {
        if (!cancelled) fn();
      }, delay);
      timersRef.current.push(id);
    };

    const runCycle = () => {
      queue(() => {
        scrollByDirection(1);
        queue(runCycle, MARQUEE_MOVE_MS + MARQUEE_HOLD_MS);
      }, MARQUEE_HOLD_MS);
    };

    runCycle();

    return () => {
      cancelled = true;
      clearTimers();
    };
  }, [canScroll, isPaused, scrollByDirection, clearTimers]);

  if (n === 0) return null;

  return (
    <div
      className={`home-manual-slider-wrap ${showNavArrows ? '' : 'home-no-nav-arrows'} ${className}`.trim()}
    >
      {canScroll && showNavArrows ? (
        <button
          type="button"
          className="home-categories-nav left"
          onClick={() => scrollByDirection(-1)}
          aria-label="Scroll left"
        >
          ‹
        </button>
      ) : null}
      <div className="home-marquee-viewport home-scroll-viewport" ref={viewportRef}>
        <div className="home-marquee-track">
          {products.map((product, idx) => {
            const productId = getProp(product, 'productId');
            return (
              <div key={`marquee-slot-${productId}-${idx}`} className="home-marquee-item">
                {renderCard(product, `marquee-${productId}-${idx}`)}
              </div>
            );
          })}
        </div>
      </div>
      {canScroll && showNavArrows ? (
        <button
          type="button"
          className="home-categories-nav right"
          onClick={() => scrollByDirection(1)}
          aria-label="Scroll right"
        >
          ›
        </button>
      ) : null}
    </div>
  );
}

// Format price with Indian locale (e.g. 16248.75 -> 16,248.75)
const formatPrice = (num) => {
  if (num == null || num === '' || Number.isNaN(Number(num))) return 'N/A';
  return Number(num).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/** Strip HTML tags and decode entities (&nbsp;, &amp;, …) for plain-text preview on cards */
const toPlainTextPreview = (raw, maxLen = 120) => {
  if (raw == null || typeof raw !== 'string') return '';
  let s = raw.replace(/<[^>]*>/g, ' ');
  if (typeof document !== 'undefined') {
    const ta = document.createElement('textarea');
    ta.innerHTML = s;
    s = ta.value;
  } else {
    s = s
      .replace(/&nbsp;/gi, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'");
  }
  s = s.replace(/\s+/g, ' ').trim();
  if (/[\u0900-\u097F\u0A80-\u0AFF\u0B80-\u0BFF\u0C00-\u0C7F\u0C80-\u0CFF\u0D00-\u0D7F]/.test(s)) return '';
  if ((s.match(/[A-Za-z]/g) || []).length < 8) return '';
  if (maxLen > 0 && s.length > maxLen) return `${s.slice(0, maxLen)}…`;
  return s;
};

const Home = () => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const subCategoriesStatus = useSelector((state) => state.categories.subCategoriesStatus);
  const categoriesWithSubCategories = useSelector((state) => state.categories.itemsWithSubCategories);
  const products = useSelector((state) => state.products.items);
  const productsStatus = useSelector((state) => state.products.status);
  const error = useSelector((state) => state.products.error);
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const userRole = useSelector((state) => state.auth.userRole);
  const cartItems = useSelector((state) => state.cart.items);

  // Get filter params from URL
  const urlCategoryId = searchParams.get('category') ? parseInt(searchParams.get('category')) : null;
  const urlSubCategoryId = searchParams.get('subcategory') ? parseInt(searchParams.get('subcategory')) : null;
  const urlTertiaryCategoryId = searchParams.get('tertiary') ? parseInt(searchParams.get('tertiary')) : null;
  const urlQuaternaryCategoryId = searchParams.get('quaternary') ? parseInt(searchParams.get('quaternary')) : null;
  const urlSearchQuery = searchParams.get('search') || null;

  const [selectedCategoryId, setSelectedCategoryId] = useState(urlCategoryId);
  const [selectedSubCategoryId, setSelectedSubCategoryId] = useState(urlSubCategoryId);
  const [selectedTertiaryCategoryId, setSelectedTertiaryCategoryId] = useState(urlTertiaryCategoryId);
  const [selectedQuaternaryCategoryId, setSelectedQuaternaryCategoryId] = useState(urlQuaternaryCategoryId);
  const [searchQuery, setSearchQuery] = useState(urlSearchQuery);
  const [productRatings, setProductRatings] = useState({});
  const [sellerProducts, setSellerProducts] = useState([]);
  const [sellerProductsLoading, setSellerProductsLoading] = useState(false);
  const [videoProducts, setVideoProducts] = useState([]);
  const [videoProductsStatus, setVideoProductsStatus] = useState('idle');
  const [homeBrands, setHomeBrands] = useState([]);
  const [homeBrandsStatus, setHomeBrandsStatus] = useState('idle');
  const [categoryImages, setCategoryImages] = useState({});
  const [categoryImageErrors, setCategoryImageErrors] = useState({});
  const [isCategoryScrollPaused, setIsCategoryScrollPaused] = useState(false);
  const [trendingStartIndex, setTrendingStartIndex] = useState(0);
  const [featuredStartIndex, setFeaturedStartIndex] = useState(0);
  const [isTrendingScrollPaused, setIsTrendingScrollPaused] = useState(false);
  const [isFeaturedScrollPaused, setIsFeaturedScrollPaused] = useState(false);
  const [isBrandScrollPaused, setIsBrandScrollPaused] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const trendingAutoScrollPausedRef = useRef(false);
  const featuredAutoScrollPausedRef = useRef(false);
  const navigate = useNavigate();
  const token = localStorage.getItem('jwtToken');

  // Sync URL params with state
  useEffect(() => {
    setSelectedCategoryId(urlCategoryId);
    setSelectedSubCategoryId(urlSubCategoryId);
    setSelectedTertiaryCategoryId(urlTertiaryCategoryId);
    setSelectedQuaternaryCategoryId(urlQuaternaryCategoryId);
    setSearchQuery(urlSearchQuery);
  }, [urlCategoryId, urlSubCategoryId, urlTertiaryCategoryId, urlQuaternaryCategoryId, urlSearchQuery]);

  useEffect(() => {
    if (subCategoriesStatus === 'idle') {
      dispatch(fetchCategoriesWithSubCategories());
    }
  }, [subCategoriesStatus, dispatch]);

  // Load cart from server when user is logged in
  useEffect(() => {
    if (isLoggedIn) {
      dispatch(fetchCart());
    }
  }, [isLoggedIn, dispatch]);

  // Fetch products - for sellers, fetch only their products
  useEffect(() => {
    if (isLoggedIn && userRole === 'Seller') {
      // Fetch seller's own products
      const fetchSellerProducts = async () => {
        setSellerProductsLoading(true);
        try {
          const response = await axios.get(`${API_URL}/ProductSellers/my-products`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const data = response.data;
          const arr = Array.isArray(data) ? data : (data?.$values ?? []);
          setSellerProducts(arr);
        } catch (err) {
          console.error('Failed to load seller products:', err);
          setSellerProducts([]);
        } finally {
          setSellerProductsLoading(false);
        }
      };
      fetchSellerProducts();
    } else {
      // For non-sellers, fetch products with optional category filters and search
      dispatch(fetchProducts({ 
        categoryId: selectedCategoryId, 
        subCategoryId: selectedSubCategoryId,
        tertiaryCategoryId: selectedTertiaryCategoryId,
        quaternaryCategoryId: selectedQuaternaryCategoryId,
        search: searchQuery
      }));
    }
  }, [dispatch, selectedCategoryId, selectedSubCategoryId, selectedTertiaryCategoryId, selectedQuaternaryCategoryId, searchQuery, isLoggedIn, userRole, token]);

  // Product videos section (guest / customer): independent of category filters
  useEffect(() => {
    const isSeller = isLoggedIn && userRole === 'Seller';
    const isAdmin = isLoggedIn && userRole === 'Admin';
    if (isSeller || isAdmin) {
      setVideoProducts([]);
      setVideoProductsStatus('idle');
      return;
    }
    let cancelled = false;
    setVideoProductsStatus('loading');
    (async () => {
      try {
        const response = await axios.get(`${API_URL}/Products/with-videos`);
        const list = normalizeList(response.data);
        if (!cancelled) {
          setVideoProducts(list);
          setVideoProductsStatus('succeeded');
        }
      } catch (e) {
        console.error('Failed to load product videos:', e);
        if (!cancelled) {
          setVideoProducts([]);
          setVideoProductsStatus('failed');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isLoggedIn, userRole]);

  useEffect(() => {
    const isSeller = isLoggedIn && userRole === 'Seller';
    const isAdmin = isLoggedIn && userRole === 'Admin';
    if (isSeller || isAdmin) {
      setHomeBrands([]);
      setHomeBrandsStatus('idle');
      return;
    }

    let cancelled = false;
    setHomeBrandsStatus('loading');

    (async () => {
      try {
        const response = await axios.get(`${API_URL}/Products/home-brands`);
        const list = normalizeList(response.data);
        if (!cancelled) {
          setHomeBrands(list);
          setHomeBrandsStatus('succeeded');
        }
      } catch (e) {
        console.error('Failed to load home brands:', e);
        if (!cancelled) {
          setHomeBrands([]);
          setHomeBrandsStatus('failed');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isLoggedIn, userRole]);

  useEffect(() => {
    const onScroll = () => setShowScrollTop(window.scrollY > 360);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Keep category image behavior aligned with Shop By Category page:
  // category image first, then first product image fallback per category.
  useEffect(() => {
    if (!categoriesWithSubCategories || categoriesWithSubCategories.length === 0) return;
    let cancelled = false;
    const fetchCategoryFallbackImages = async () => {
      const images = {};
      for (const cat of categoriesWithSubCategories) {
        try {
          const res = await axios.get(`${API_URL}/Products`, {
            params: { categoryId: cat.categoryId }
          });
          const arr = Array.isArray(res.data) ? res.data : (res.data?.$values ?? []);
          const first = arr.find((p) => getProp(p, 'imageUrl'));
          if (first) images[cat.categoryId] = getImageUrl(getProp(first, 'imageUrl'));
        } catch {
          // ignore per-category image lookup failures
        }
      }
      if (!cancelled) setCategoryImages(images);
    };
    fetchCategoryFallbackImages();
    return () => {
      cancelled = true;
    };
  }, [categoriesWithSubCategories]);

  useEffect(() => {
    // Fetch review summaries for all products
    const fetchRatings = async () => {
      if (products.length > 0) {
        const ratings = {};
        for (const product of products) {
          const productId = getProp(product, 'productId');
          try {
            const response = await axios.get(`${API_URL}/Reviews/product/${productId}/summary`);
            ratings[productId] = response.data;
          } catch (error) {
            // Product might not have reviews yet
            ratings[productId] = { averageRating: 0, totalReviews: 0 };
          }
        }
        setProductRatings(ratings);
      }
    };
    if (products.length > 0) {
      fetchRatings();
    }
  }, [products]);

  const handleAddToCart = (product, e) => {
    if (e) e.stopPropagation();
    const productId = getProp(product, 'productId');
    const productName = getProp(product, 'productName');
    const price = getProp(product, 'price');
    const originalPrice = getProp(product, 'originalPrice') ?? null;
    const imageUrl = getImageUrl(getProp(product, 'imageUrl'));
    if (getProductVariants(product).length > 0) {
      navigate(`/product/${productId}`);
      return;
    }
    const available = getEffectiveStock(product);
    if (getCartQuantity(productId) >= available) {
      window.alert(available > 0 ? `Only ${available} in stock.` : 'This item is out of stock.');
      return;
    }
    
    if (isLoggedIn) {
      // Save to server if logged in - async thunk handles state update
      dispatch(addToCartAsync({ productId, quantity: 1 }));
    } else {
      // Save locally if not logged in
      dispatch(addToCart({ productId, productName, price, originalPrice, imageUrl, stock: available }));
    }
  };

  // Get quantity of a product in cart
  const getCartQuantity = (productId) => {
    const cartItem = cartItems.find(item => item.productId === productId);
    return cartItem ? cartItem.quantity : 0;
  };

  // Handle increment quantity
  const handleIncrement = (product, e) => {
    if (e) e.stopPropagation();
    const productId = getProp(product, 'productId');
    if (getProductVariants(product).length > 0) {
      navigate(`/product/${productId}`);
      return;
    }
    const currentQty = getCartQuantity(productId);
    const available = getEffectiveStock(product);
    if (currentQty >= available) {
      window.alert(available > 0 ? `Only ${available} in stock.` : 'This item is out of stock.');
      return;
    }
    
    if (isLoggedIn) {
      dispatch(updateCartItemAsync({ productId, quantity: currentQty + 1 }));
    } else {
      dispatch(updateQuantity({ productId, quantity: currentQty + 1, stock: available }));
    }
  };

  // Handle decrement quantity
  const handleDecrement = (product, e) => {
    if (e) e.stopPropagation();
    const productId = getProp(product, 'productId');
    const currentQty = getCartQuantity(productId);
    
    if (currentQty <= 1) {
      // Remove from cart
      if (isLoggedIn) {
        dispatch(removeFromCartAsync(productId));
      } else {
        dispatch(removeFromCart(productId));
      }
    } else {
      // Decrement quantity
      if (isLoggedIn) {
        dispatch(updateCartItemAsync({ productId, quantity: currentQty - 1 }));
      } else {
        dispatch(updateQuantity({ productId, quantity: currentQty - 1 }));
      }
    }
  };

  // Handle remove from cart
  const handleRemoveFromCart = (product, e) => {
    if (e) e.stopPropagation();
    const productId = getProp(product, 'productId');
    
    if (isLoggedIn) {
      dispatch(removeFromCartAsync(productId));
    } else {
      dispatch(removeFromCart(productId));
    }
  };

  const handleProductClick = (product) => {
    const productId = getProp(product, 'productId');
    navigate(`/product/${productId}`);
  };

  const refreshProductListing = () => {
    dispatch(fetchProducts({
      categoryId: selectedCategoryId,
      subCategoryId: selectedSubCategoryId,
      tertiaryCategoryId: selectedTertiaryCategoryId,
      quaternaryCategoryId: selectedQuaternaryCategoryId,
      search: searchQuery,
    }));
  };

  const handleSoftDeleteProduct = async (product) => {
    const productId = getProp(product, 'productId');
    const productName = getProp(product, 'productName') || `ID ${productId}`;
    if (!window.confirm(`Soft delete "${productName}"?`)) return;
    try {
      await axios.delete(`${API_URL}/Products/${productId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      refreshProductListing();
    } catch (err) {
      const msg = err.response?.data?.message ?? err.response?.data ?? 'Failed to delete product.';
      alert(typeof msg === 'string' ? msg : 'Failed to delete product.');
    }
  };


  // Determine which products to show
  const isSeller = isLoggedIn && userRole === 'Seller';
  const isAdmin = isLoggedIn && userRole === 'Admin';
  const displayProducts = isSeller ? sellerProducts : products;
  const isLoading = isSeller ? sellerProductsLoading : productsStatus === 'loading';
  const hasError = !isSeller && productsStatus === 'failed';
  const isCustomerView = !isSeller && !isAdmin;

  // Customer category links open dedicated PLP (/category/:id)
  useEffect(() => {
    if (!isCustomerView || !urlCategoryId) return;
    const q = new URLSearchParams();
    if (urlSubCategoryId) q.set('subcategory', String(urlSubCategoryId));
    if (urlTertiaryCategoryId) q.set('tertiary', String(urlTertiaryCategoryId));
    if (urlQuaternaryCategoryId) q.set('quaternary', String(urlQuaternaryCategoryId));
    if (urlSearchQuery) q.set('search', urlSearchQuery);
    const qs = q.toString();
    navigate(`/category/${urlCategoryId}${qs ? `?${qs}` : ''}`, { replace: true });
  }, [
    isCustomerView,
    urlCategoryId,
    urlSubCategoryId,
    urlTertiaryCategoryId,
    urlQuaternaryCategoryId,
    urlSearchQuery,
    navigate,
  ]);

  const categoryTiles = (categoriesWithSubCategories || []).slice(0, 12);
  const trendingProducts = (displayProducts || []).filter((p) => Boolean(p?.isTrending));
  const featuredProductsList = (displayProducts || []).filter((p) => Boolean(p?.isFeatured));

  const scrollTrendingSlider = useCallback((direction) => {
    const len = trendingProducts.length;
    if (len < 2) return;
    setTrendingStartIndex((prev) => {
      const next = direction > 0 ? prev + 1 : prev - 1;
      return (next + len) % len;
    });
  }, [trendingProducts.length]);

  const scrollFeaturedSlider = useCallback((direction) => {
    const len = featuredProductsList.length;
    if (len < 2) return;
    setFeaturedStartIndex((prev) => {
      const next = direction > 0 ? prev + 1 : prev - 1;
      return (next + len) % len;
    });
  }, [featuredProductsList.length]);

  const orderedTrendingProducts = useMemo(() => {
    if (trendingProducts.length <= 1) return trendingProducts;
    return trendingProducts.map((_, i) => trendingProducts[(trendingStartIndex + i) % trendingProducts.length]);
  }, [trendingProducts, trendingStartIndex]);

  const continuousTrendingProducts = useMemo(() => {
    if (orderedTrendingProducts.length <= 1) return orderedTrendingProducts;
    return [...orderedTrendingProducts, ...orderedTrendingProducts];
  }, [orderedTrendingProducts]);

  const orderedFeaturedProducts = useMemo(() => {
    if (featuredProductsList.length <= 1) return featuredProductsList;
    return featuredProductsList.map((_, i) =>
      featuredProductsList[(featuredStartIndex + i) % featuredProductsList.length]
    );
  }, [featuredProductsList, featuredStartIndex]);

  const continuousFeaturedProducts = useMemo(() => {
    if (orderedFeaturedProducts.length <= 1) return orderedFeaturedProducts;
    return [...orderedFeaturedProducts, ...orderedFeaturedProducts];
  }, [orderedFeaturedProducts]);

  const renderCustomerProductCard = (product, indexKey) => {
    const productId = getProp(product, 'productId');
    const productName = getProp(product, 'productName');
    const price = getProp(product, 'price');
    const originalPrice = getProp(product, 'originalPrice') ?? product.originalPrice;
    const imageUrl = getImageUrl(getProp(product, 'imageUrl'));
    const numPrice = Number(price);
    const numOriginal = originalPrice != null && originalPrice !== '' ? Number(originalPrice) : null;
    const displayMrp = numOriginal != null && !Number.isNaN(numOriginal) && numOriginal > 0 ? numOriginal : numPrice;
    const qty = getCartQuantity(productId);

    return (
      <article key={indexKey} className="home-product-card">
        <button
          type="button"
          className="home-product-image-btn"
          onClick={() => handleProductClick(product)}
          aria-label={productName}
        >
          <img
            src={imageUrl}
            alt={productName}
            className="home-product-image"
            onError={(e) => { e.target.src = BROKEN_IMAGE_PLACEHOLDER; }}
          />
        </button>
        <button
          type="button"
          className="home-product-name"
          onClick={() => handleProductClick(product)}
        >
          {productName}
        </button>
        <p className="home-product-mrp">
          <span className="home-mrp-label">MRP:</span>
          <span className="home-mrp-value"> ₹{formatPrice(displayMrp)}</span>
        </p>
        {qty > 0 ? (
          <div className="home-cart-actions">
            <div className="quantity-selector home-qty-selector">
              <button
                type="button"
                className="qty-btn delete-btn"
                onClick={(e) => handleRemoveFromCart(product, e)}
                title="Remove from cart"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                </svg>
              </button>
              <span className="qty-value">{qty}</span>
              <button
                type="button"
                className="qty-btn add-btn"
                onClick={(e) => handleIncrement(product, e)}
                title="Add more"
              >
                +
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            className="home-add-cart-btn"
            onClick={(e) => handleAddToCart(product, e)}
            disabled={userRole === 'Admin'}
          >
            <span>Add to cart</span>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16" aria-hidden="true">
              <circle cx="9" cy="21" r="1" />
              <circle cx="20" cy="21" r="1" />
              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
            </svg>
          </button>
        )}
      </article>
    );
  };

  const renderSectionTitle = (title, subtitle) => (
    <div className="home-section-heading">
      <h4 className="home-section-title">
        <span>{title}</span>
      </h4>
      {subtitle ? <p className="home-section-subtitle">{subtitle}</p> : null}
    </div>
  );

  // Calculate summary stats for admin
  const totalProducts = displayProducts.length;
  const activeProducts = displayProducts.filter((p) => getEffectiveStock(p) > 0).length;
  const outOfStockProducts = totalProducts - activeProducts;

  let productContent;
  if (isCustomerView) {
    productContent = null;
  } else if (isLoading) {
    productContent = <div className="loading-message">Loading products...</div>;
  } else if (hasError) {
    productContent = <div className="error-message">Error loading products: {error}</div>;
  } else if (displayProducts.length === 0) {
    productContent = (
      <div className="no-products-message">
        {isSeller ? (
          <p>You don't have any products assigned yet. Contact admin to get products assigned to your seller account.</p>
        ) : (
          <p>No products available.</p>
        )}
      </div>
    );
  } else if (isAdmin) {
    // Admin Grid/Table View
    productContent = (
      <div className="admin-product-management">
        {/* Summary Cards */}
        <div className="summary-cards">
          <div className="summary-card">
            <span className="summary-number">{totalProducts}</span>
            <span className="summary-label">Total Products</span>
          </div>
          <div className="summary-card active">
            <span className="summary-number">{activeProducts}</span>
            <span className="summary-label">In Stock</span>
          </div>
          <div className="summary-card warning">
            <span className="summary-number">{outOfStockProducts}</span>
            <span className="summary-label">Out of Stock</span>
          </div>
        </div>

        {/* Product Table */}
        <div className="product-table-container">
          <table className="product-table">
            <thead>
              <tr>
                <th className="col-product">Product Details</th>
                <th className="col-price">Price</th>
                <th className="col-seller">Seller</th>
                <th className="col-color">Color</th>
                <th className="col-size">Size</th>
                <th className="col-stock">Stock</th>
                <th className="col-category">Category</th>
                <th className="col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayProducts.map((product) => {
                const productId = getProp(product, 'productId');
                const productName = getProp(product, 'productName');
                const price = getProp(product, 'price');
                const originalPrice = getProp(product, 'originalPrice');
                const sellerPrice = getProp(product, 'sellerPrice');
                const sellerNameForAdmin = getProp(product, 'assignedSellerName') || getProp(product, 'sellerName');
                const imageUrl = getProp(product, 'imageUrl');
                const stock = getEffectiveStock(product);
                const colorNames = getProductColorNames(product);
                const sizeNames = getProductSizeNames(product);
                const variantStockGroups = getVariantStockGroups(product);
                const categoryName = getProp(product, 'categoryName');
                const subCategoryName = getProp(product, 'subCategoryName');
                const showDiscount = originalPrice != null && Number(originalPrice) > Number(price) && Number(originalPrice) > 0;
                const discountPercent = showDiscount ? Math.round((1 - Number(price) / Number(originalPrice)) * 100) : 0;

                return (
                  <tr key={product.$id || productId}>
                    <td className="col-product">
                      <div className="product-cell">
                        <img 
                          src={getImageUrl(imageUrl)} 
                          alt={productName}
                          onError={(e) => { e.target.src = BROKEN_IMAGE_PLACEHOLDER; }}
                        />
                        <div className="product-info">
                          <span className="product-name" onClick={() => handleProductClick(product)}>
                            {productName}
                          </span>
                          <span className="product-id">ID: {productId}</span>
                        </div>
                      </div>
                    </td>
                    <td className="col-price">
                      <div className="price-cell">
                        {showDiscount && (
                          <>
                            <span className="original-price">₹{formatPrice(originalPrice)}</span>
                            <span className="discount-badge">{discountPercent}% off</span>
                          </>
                        )}
                        <span className="base-price">₹{formatPrice(price)}</span>
                        {sellerPrice && sellerPrice !== price && (
                          <span className="seller-price">Seller: ₹{formatPrice(sellerPrice)}</span>
                        )}
                      </div>
                    </td>
                    <td className="col-seller">
                      {sellerNameForAdmin ? (
                        <span className="seller-badge">{sellerNameForAdmin}</span>
                      ) : (
                        <span className="no-seller">No Seller</span>
                      )}
                    </td>
                    <td className="col-color">
                      {colorNames.length > 0 ? (
                        <div className="variant-name-list">
                          {colorNames.map((name) => (
                            <span key={name} className="variant-name-chip">{name}</span>
                          ))}
                        </div>
                      ) : (
                        <span className="no-variant">—</span>
                      )}
                    </td>
                    <td className="col-size">
                      {sizeNames.length > 0 ? (
                        <div className="variant-name-list">
                          {sizeNames.map((name) => (
                            <span key={name} className="variant-name-chip">{name}</span>
                          ))}
                        </div>
                      ) : (
                        <span className="no-variant">—</span>
                      )}
                    </td>
                    <td className="col-stock">
                      {variantStockGroups.length > 0 ? (
                        <div className="variant-stock">
                          {variantStockGroups.map((group) => (
                            <div key={group.color} className="variant-stock-color">
                              <span className="variant-stock-color-name">{group.color}</span>
                              <div className="variant-stock-sizes">
                                {group.sizes.map((row, index) => (
                                  <span
                                    key={`${group.color}-${row.size || 'stock'}-${index}`}
                                    className={`variant-stock-chip ${row.stock > 0 ? 'in-stock' : 'out-of-stock'}`}
                                  >
                                    {row.size ? `${row.size}: ${row.stock}` : row.stock}
                                  </span>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className={`stock-badge ${stock > 0 ? 'in-stock' : 'out-of-stock'}`}>
                          {stock > 0 ? stock : 'Out of Stock'}
                        </span>
                      )}
                    </td>
                    <td className="col-category">
                      <div className="category-cell">
                        <span className="category-name">{categoryName || '-'}</span>
                        {subCategoryName && <span className="subcategory-name">{subCategoryName}</span>}
                      </div>
                    </td>
                    <td className="col-actions">
                      <div className="action-buttons">
                      <button 
                        className="action-btn edit-btn"
                        onClick={() => navigate(`/admin/products/${productId}/edit`)}
                        title="Edit Product"
                      >
                        <span className="action-icon" aria-hidden="true">✏️</span>
                        <span className="action-label">Edit</span>
                      </button>
                      <button 
                        className="action-btn view-btn"
                        onClick={() => handleProductClick(product)}
                        title="View Details"
                      >
                        <span className="action-icon" aria-hidden="true">👁️</span>
                        <span className="action-label">View</span>
                      </button>
                      <button 
                        className="action-btn delete-btn"
                        onClick={() => handleSoftDeleteProduct(product)}
                        title="Soft Delete Product"
                      >
                        <span className="action-icon" aria-hidden="true">🗑️</span>
                        <span className="action-label">Delete</span>
                      </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  } else {
    // Seller Card View
    productContent = (
      <div className="product-list">
        {displayProducts.map((product) => {
          const productId = product.productId;
          const productName = product.productName;
          const rawSnippet = product.shortDescription;
          const description = toPlainTextPreview(rawSnippet, 120);
          const price = product.sellerPrice;
          const imageUrl = product.productImage;
          const stockQty = product.stockQuantity;
          const isActive = product.isActive;

          return (
            <div key={product.productSellerId || product.$id || productId} className={`product-card ${!isActive ? 'inactive' : ''}`}>
              <div
                className="product-card-content"
                onClick={() => handleProductClick(product)}
                style={{ cursor: 'pointer' }}
              >
                {!isActive && <div className="inactive-badge">Inactive</div>}
                <img
                  src={getImageUrl(imageUrl)}
                  alt={productName}
                  className="product-image"
                  onError={(e) => { e.target.src = BROKEN_IMAGE_PLACEHOLDER; }}
                />
                <h3>{productName}</h3>
                {description ? <p className="product-card-description">{description}</p> : null}
                <div className="price-block">
                  <span className="price">₹{formatPrice(price)}</span>
                </div>
                {stockQty !== null && (
                  <p className="stock-info">Stock: {stockQty} units</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  const showProductVideosSection = !isSeller && !isAdmin;

  return (
    <div className={`home-wrapper${isCustomerView ? ' home-customer-layout' : ''}`}>
      <Header />
      {!isSeller && !isAdmin && <HomeSlider />}
      <div className={`home-container ${isAdmin ? 'admin-view' : ''}${isCustomerView ? ' home-customer-container' : ''}`}>
        <div className="home-content-stack">
          {isCustomerView && (
            <section className="home-highlights-shell home-shell" aria-label="Home highlights">
              <div className="home-highlights-section home-section home-categories-section">
                {renderSectionTitle('Categories', 'Find Exactly What You Need')}
                <div
                  className="home-categories-slider-wrap home-no-nav-arrows"
                  onMouseEnter={() => setIsCategoryScrollPaused(true)}
                  onMouseLeave={() => setIsCategoryScrollPaused(false)}
                >
                  <HomeCategoriesRow
                    categories={categoryTiles}
                    categoryImages={categoryImages}
                    categoryImageErrors={categoryImageErrors}
                    onCategoryImageError={(categoryId) => {
                      setCategoryImageErrors((prev) => ({ ...prev, [categoryId]: true }));
                    }}
                    isPaused={isCategoryScrollPaused}
                  />
                </div>
              </div>

              {isLoading && (
                <div className="home-section home-status-msg">Loading products…</div>
              )}
              {hasError && (
                <div className="home-section home-status-msg home-status-error">
                  Could not load products. Please refresh the page.
                </div>
              )}
              {!isLoading && !hasError && trendingProducts.length > 0 && (
                <div
                  className="home-highlights-section home-section home-marquee-section"
                  onMouseEnter={() => {
                    trendingAutoScrollPausedRef.current = true;
                    setIsTrendingScrollPaused(true);
                  }}
                  onMouseLeave={() => {
                    trendingAutoScrollPausedRef.current = false;
                    setIsTrendingScrollPaused(false);
                  }}
                >
                  {renderSectionTitle('Trending Products')}
                  <HomeProductMarqueeRow
                    products={trendingProducts}
                    isPaused={isTrendingScrollPaused}
                    renderCard={renderCustomerProductCard}
                    showNavArrows={false}
                  />
                </div>
              )}

              {homeBrandsStatus === 'succeeded' && homeBrands.length > 0 && (
                <div className="home-highlights-section home-brands-section home-section">
                  {renderSectionTitle('Our Brands', 'Explore Our Exclusive Brands')}
                  <div
                    className="home-manual-slider-wrap home-brands-slider-wrap home-no-nav-arrows"
                    onMouseEnter={() => setIsBrandScrollPaused(true)}
                    onMouseLeave={() => setIsBrandScrollPaused(false)}
                  >
                    <HomeBrandsRow brands={homeBrands} isPaused={isBrandScrollPaused} />
                  </div>
                </div>
              )}

              {!isLoading && !hasError && featuredProductsList.length > 0 && (
                <div
                  className="home-highlights-section home-section home-marquee-section"
                  onMouseEnter={() => {
                    featuredAutoScrollPausedRef.current = true;
                    setIsFeaturedScrollPaused(true);
                  }}
                  onMouseLeave={() => {
                    featuredAutoScrollPausedRef.current = false;
                    setIsFeaturedScrollPaused(false);
                  }}
                >
                  {renderSectionTitle('Featured Products')}
                  <HomeProductMarqueeRow
                    products={featuredProductsList}
                    isPaused={isFeaturedScrollPaused}
                    renderCard={renderCustomerProductCard}
                    showNavArrows={false}
                  />
                </div>
              )}

              {showProductVideosSection && (
                <div
                  className="home-highlights-section home-section home-videos-section"
                  aria-label="Product videos"
                >
                  {renderSectionTitle('Videos')}
                  {videoProductsStatus === 'loading' && (
                    <div className="home-status-msg">Loading videos…</div>
                  )}
                  {videoProductsStatus === 'failed' && (
                    <p className="home-status-msg home-status-error">
                      Videos could not be loaded. Please try again later.
                    </p>
                  )}
                  {videoProductsStatus === 'succeeded' && videoProducts.length === 0 && (
                    <p className="home-status-msg">No product videos yet. Check back soon.</p>
                  )}
                  {videoProductsStatus === 'succeeded' && videoProducts.length > 0 && (
                    <HomeVideosCarousel products={videoProducts} />
                  )}
                </div>
              )}
            </section>
          )}
          {!isCustomerView && productContent != null && (
            <div className="product-section full-width">
              {productContent}
            </div>
          )}
        </div>
      </div>
      <Footer />
      {isCustomerView && showScrollTop && (
        <button
          type="button"
          className="home-scroll-top-btn"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          aria-label="Scroll to top"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="22" height="22" aria-hidden="true">
            <path d="M12 19V5M5 12l7-7 7 7" />
          </svg>
        </button>
      )}
    </div>
  );
};

export default Home;
